import qs from 'querystring';
import { QUERY_PARAMS } from '@/constants/urlQueryParams';
import {
  UrlSearchApiOptionsExecutionProps,
  ParamsObjectProps,
} from './urlQueryParams.d';

const {
  CCODE,
  COLOR,
  DELIVERY_DATE,
  END_DATE,
  FLOWER_TYPE,
  MCODE,
  OCCASION,
  PAGE,
  SORT,
  START_DATE,
  AVAILABILITY_VERSION,
  ZIP_CODE,
} = QUERY_PARAMS;
/* 
  Groupings of query params that are permitted for specific endpoint requests 
  Used to prevent misc params being passed to API endpoint requests
*/
export const allowlistedQueryParams = {
  global: [CCODE],
  marketing: [CCODE, MCODE],
  category: [
    CCODE,
    COLOR,
    DELIVERY_DATE,
    END_DATE,
    FLOWER_TYPE,
    MCODE,
    OCCASION,
    PAGE,
    SORT,
    START_DATE,
    AVAILABILITY_VERSION,
    ZIP_CODE,
  ],
};

/**
 * Wrapper for URLSearchParams web API
 * https://developer.mozilla.org/en-US/docs/Web/API/URLSearchParams
 *
 * @method
 * @name _urlSearchApiOptionsExecution
 *
 * @param {object} urlParamsApi - URLSearchParams object instance
 * @param {string} options - object
 */
function _urlSearchApiOptionsExecution(
  urlParamsApi: URLSearchParams,
  options: UrlSearchApiOptionsExecutionProps
): string | string[] | URLSearchParams {
  switch (options.operation) {
    case 'append':
      urlParamsApi.append(options.key, options.value);
      return urlParamsApi.toString();
    case 'delete':
      urlParamsApi.delete(options.key);
      return urlParamsApi.toString();
    case 'set':
      urlParamsApi.set(options.key, options.value);
      return urlParamsApi.toString();
    case 'get':
      return urlParamsApi.get(options.key);
    case 'getAll':
      return urlParamsApi.getAll(options.key);
    default:
      return urlParamsApi.toString();
  }
}

/**
 * Merges, de-duplicates, and sorts two URL query strings by key
 *
 * @method
 * @name stripDuplicates
 *
 * @param {string} newQstr - new incoming string
 * @param {string} currentQStr - existing string
 */
export function stripDuplicates(newQStr: string, currentQStr = ''): string {
  const incomingQuery = new URLSearchParams(newQStr);
  const currentQuery = new URLSearchParams(currentQStr);
  const dedupedQs = new URLSearchParams('');

  [incomingQuery, currentQuery].map(params => {
    params.forEach((value, key) => {
      const valuesWithSameKey = dedupedQs.getAll(key);
      const isDuplicate = valuesWithSameKey.indexOf(value) > -1;

      if (!isDuplicate) {
        dedupedQs.append(key, value);
      }
    });
  });

  dedupedQs.sort();

  return dedupedQs.toString();
}

/**
 * Removes encoded characters from a string
 *
 * @method
 * @name prettyQueryParams
 *
 * @param {string} queryString - URL query string
 */
export function prettyQueryParams(queryString: string): string {
  const prettifiedQueries = queryString
    ? decodeURI(queryString).replace(/\++/g, ' ')
    : queryString;

  return prettifiedQueries;
}

/**
 * Filters out blocklisted/disallowed URL query parameters based on allowlistedQueryParams
 *
 * @method
 * @name getValidQs
 *
 * @param {string} queryString - URL query string
 * @param {string[]} allowList - array of strings expected to be from allowlistedQueryParams
 */
export function getValidQs(queryString: string, allowlist: string[]): string {
  const urlApi = new URLSearchParams(queryString);
  const qKeysArr = Array.from(urlApi.keys());

  for (const key of qKeysArr) {
    if (allowlist.indexOf(key) === -1) {
      urlApi.delete(key);
    }
  }

  return urlApi.toString();
}

/**
 * Accepts a URL string and returns either the queries string or a URLSearchParams object instance
 *
 * @method
 * @name getParamsVal
 *
 * @param {string} urlPart - URL string or URL path string or URL query string
 * @param {object} options - options to perform operations using URLSearchParams web API
 * @param {boolean} returnQueryString - whether to return a string or object
 */
export function getParamsVal(
  urlPart: string,
  options: UrlSearchApiOptionsExecutionProps | null = null,
  returnQueryString = false
): string | string[] | URLSearchParams {
  let urlParamsApi;

  try {
    urlParamsApi = new URL(urlPart).searchParams;
  } catch {
    const params =
      urlPart && urlPart.includes('?') ? urlPart.split('?').pop() : urlPart;

    urlParamsApi = new URLSearchParams(params);
  }

  if (options?.operation) {
    return _urlSearchApiOptionsExecution(urlParamsApi, options);
  }

  return returnQueryString ? urlParamsApi.toString() : urlParamsApi;
}

export function getParamsObject(url: string): ParamsObjectProps {
  const rawQs = getParamsVal(url, null, true) as string;

  return qs.decode(rawQs);
}
